const DEFAULT_SAVE_DELAY = 2000

const getService = (context, name) => {
    const service = context.services && context.services[name]

    if (typeof service !== 'function') {
        throw new Error(`Other Information requires services.${name}()`)
    }

    return service
}

const createOtherInformationController = ({
    context,
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    saveDelay = DEFAULT_SAVE_DELAY,
}) => {
    let validation = { isValid: false, errors: [] }
    let saveTimeoutId = null
    let pendingSave = null
    let revision = 0
    let savedRevision = 0
    let disposed = false

    const publishDraft = () => Promise.resolve(publishEffect({
        type: 'request.other-information.changed',
        phase: 'draft',
        scopes: ['request.other-information'],
    })).catch(reportError)

    function schedulePendingRevision() {
        if (disposed || saveTimeoutId !== null || savedRevision >= revision) {
            return
        }

        saveTimeoutId = setTimeout(() => {
            saveTimeoutId = null
            // The save function is declared below so both helpers can reschedule each other.
            // eslint-disable-next-line no-use-before-define
            performSave().catch(() => undefined)
        }, saveDelay)
    }

    function performSave() {
        if (pendingSave || savedRevision >= revision) {
            return pendingSave || Promise.resolve()
        }

        const savingRevision = revision
        setSaving(true)

        const operation = Promise.resolve()
            .then(() => getService(context, 'saveReceivedRequest')())
            .then(() => publishEffect({
                type: 'request.other-information.changed',
                phase: 'committed',
                scopes: ['request.other-information'],
            }))
            .then(() => {
                savedRevision = Math.max(savedRevision, savingRevision)
                setDirty(savedRevision < revision)
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                if (pendingSave === operation) {
                    pendingSave = null
                    setSaving(false)
                    if (savedRevision >= savingRevision) {
                        schedulePendingRevision()
                    }
                }
            })

        pendingSave = operation
        return operation
    }

    const scheduleSave = otherInformation => {
        getService(context, 'changeOtherInformation')(otherInformation)
        revision += 1
        setDirty(true)
        publishDraft()
        clearTimeout(saveTimeoutId)
        saveTimeoutId = null
        schedulePendingRevision()
    }

    return Object.freeze({
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        handleAction(action) {
            if (disposed) {
                return Promise.resolve()
            }

            if (action.type !== 'other-information.change') {
                throw new Error(`Unsupported Other Information action "${action.type}"`)
            }

            scheduleSave(action.payload.otherInformation)
            return Promise.resolve()
        },
        async flush() {
            clearTimeout(saveTimeoutId)
            saveTimeoutId = null

            while (pendingSave || savedRevision < revision) {
                clearTimeout(saveTimeoutId)
                saveTimeoutId = null
                if (pendingSave) {
                    // Revisions are intentionally persisted sequentially so the latest edit wins.
                    // eslint-disable-next-line no-await-in-loop
                    await pendingSave
                } else {
                    // eslint-disable-next-line no-await-in-loop
                    await performSave()
                }
            }

            await getService(context, 'refreshBulkReplyCandidates')()
            await publishEffect({
                type: 'request.bulk-reply-candidates.changed',
                phase: 'committed',
                scopes: ['request.bulk-reply-candidates'],
            })
        },
        dispose() {
            disposed = true
            clearTimeout(saveTimeoutId)
            saveTimeoutId = null
        },
    })
}

export {
    DEFAULT_SAVE_DELAY,
    createOtherInformationController,
}
export default createOtherInformationController
